"use client";

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { parse } from "date-fns";
import { Trash2 } from "lucide-react";
import { useParams } from "next/navigation";
import { createPortal } from "react-dom";
import { useForm, type Control } from "react-hook-form";
import { toast } from "sonner";

import { getAgentsByOrgId } from "@/actions/agents";
import { deleteWork, getWork, updateWork } from "@/actions/work";
import type { AgentModel, OrgProfileModel, WorkModel } from "@/api";
import { WorkAccessLevelBadgeDropdown } from "@/components/dashboard/forms/work-access-level-badge-dropdown";
import {
  DatePickerField,
  DocumentationField,
  NumberField,
  SelectField,
  TextareaField,
} from "@/components/dashboard/forms/work-form-fields";
import ComboboxFormField from "@/components/form-elements/combobox-form-field";
import { ProjectFunctionAssignmentField } from "@/components/project/project-function-assignment-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { queryKeys } from "@/constants/query";
import useOrgSettings from "@/hooks/use-org-settings";
import { usePeople } from "@/hooks/use-people";
import { getWorkItemAllParentOptions } from "@/hooks/use-work-items";
import { useWorkTree } from "@/hooks/use-work-tree";
import { useTableTreeData } from "@/hooks/useTableTreeData";
import { isValidOrgId } from "@/lib/utils";
import type { WorkTreeModel } from "@/models/WorkTreeModel";
import {
  WorkFormSchema,
  type WorkFormValues,
} from "@/schemas/work-form-schema";

import { DeleteConfirmation } from "../delete-confirmation";
import {
  createWorkModelFromFormValues,
  useWorkFormData,
} from "./work-form-utils";

/** Stable fallback — `|| []` allocates a new array every render and loops with useTableTreeData. */
const EMPTY_WORK_TREE_ROWS: WorkTreeModel[] = [];

interface EditWorkFormProps {
  workItem: WorkModel | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  //onWorkUpdated: () => void;
  //onFormStateChange?: (isDirty: boolean) => void;
  parentId?: string | null;
  onDelete?: () => void;
  onSuccess?: () => void;
  onTitleActionsChange?: (actions: React.ReactNode) => void;
  /**
   * Stable host element in a parent sheet header. Documentation flyout CTA is
   * portaled here (avoids storing React nodes in parent state).
   */
  documentationHeaderHostRef?: RefObject<HTMLDivElement | null>;
  /**
   * `details-docs-split`: two-column layout (details | documentation) inside the form.
   * Prefer this over portaling docs into parent state (avoids update loops).
   */
  layoutMode?: "default" | "details-docs-split";
  /**
   * Rendered under the details card in the left column (e.g. Estimates) so the
   * documentation column can stretch to match details + this footer.
   */
  leftColumnFooter?: React.ReactNode;
  /** When slotted out / split layout, use inline editor vs flyout link. */
  documentationVariant?: "flyout" | "inline";
  workAccessLevels?: OrgProfileModel[];
  isWorkAccessLevelsLoading?: boolean;
}

// TextInputField component for name
function TextInputField({
  control,
  name,
  label,
  placeholder,
}: {
  control: Control<WorkFormValues>;
  name: keyof WorkFormValues;
  label: string;
  placeholder: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              placeholder={placeholder}
              {...field}
              value={
                field.value === null || field.value === undefined
                  ? ""
                  : String(field.value)
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function EditWorkForm({
  workItem,
  open,
  onOpenChange,
  onDelete,
  onSuccess,
  onTitleActionsChange,
  documentationHeaderHostRef,
  layoutMode = "default",
  leftColumnFooter,
  documentationVariant = "flyout",
  workAccessLevels: externalWorkAccessLevels,
  isWorkAccessLevelsLoading: externalIsWorkAccessLevelsLoading,
}: EditWorkFormProps) {
  // Helper to normalize a date value that may be a Date or a string (ISO or yyyy-MM-dd)
  const toDate = (d?: unknown): Date | null => {
    if (!d) return null;
    if (d instanceof Date) return d;
    if (typeof d === "string") {
      return d.includes("T") ? new Date(d) : parse(d, "yyyy-MM-dd", new Date());
    }
    return null;
  };
  const [workItemId, setWorkItemId] = useState(workItem?.id);
  const params = useParams();
  const orgId = params?.orgId as string;
  const projectId = params?.projectId as string;
  const queryClient = useQueryClient();
  const { people } = usePeople();
  const { data: agents = [] } = useQuery<AgentModel[]>({
    queryKey: ["agents", orgId],
    queryFn: () => getAgentsByOrgId(orgId),
    enabled: isValidOrgId(orgId),
    staleTime: 60_000,
  });
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const {
    workTypes,
    workStatuses,
    workAccessLevels,
    isWorkTypesLoading,
    isWorkStatusesLoading,
    isWorkAccessLevelsLoading,
  } = useWorkFormData();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showSecurityChangeDialog, setShowSecurityChangeDialog] =
    useState(false);
  const [pendingAccessLevelId, setPendingAccessLevelId] = useState<
    string | null
  >(null);
  const [functionFacetId, setFunctionFacetId] = useState<string | null>(
    workItem?.productId ?? null,
  );
  const settings = useOrgSettings();
  const { data } = useWorkTree();
  const { tableData } = useTableTreeData({
    data: data?.workTreeModel.subRows ?? EMPTY_WORK_TREE_ROWS,
  });

  const parentOptions = getWorkItemAllParentOptions(
    tableData
      ? [
          {
            id: data?.workTreeModel.id || "",
            name: data?.workTreeModel.name || "",
          },
          ...tableData,
        ]
      : [],
    workItem || {},
  );

  // Don't derive parent object eagerly; compute inside reset to avoid frequent re-creations
  // We still expose helpers for reading current form/watch state below.

  // Compute initial parent option from current workItem to avoid using global window.parent
  const initialParentOption =
    parentOptions.find((item) => item.value === workItem?.parentId) ?? null;

  // Find default "Private" access level for create mode
  const privateAccessLevelId =
    workAccessLevels?.find((level) => level.name?.toLowerCase() === "private")
      ?.id || null;

  const form = useForm<WorkFormValues, unknown, WorkFormValues>({
    resolver: zodResolver(WorkFormSchema),
    defaultValues: {
      name: workItem?.name || "",
      workTypeId: workItem?.workTypeId || "",
      tentativeStartDate: toDate(workItem?.tentativeStartDate),
      tentativeEndDate: toDate(workItem?.tentativeEndDate),
      actualStartDate: toDate(workItem?.actualStartDate),
      actualEndDate: toDate(workItem?.actualEndDate),
      tentativeBudget: workItem?.tentativeBudget || null,
      approvedBudget: workItem?.approvedBudget || null,
      description: workItem?.description || "",
      documentation: workItem?.documentation ?? undefined,
      assignedTo: workItem?.assignedToAgent
        ? {
            value: `agent:${workItem.assignedToAgent}`,
            label:
              agents.find((a) => a.id === workItem.assignedToAgent)?.name ||
              "Agent",
          }
        : workItem?.assignedTo
          ? {
              value: `person:${workItem.assignedTo}`,
              label:
                people.find((p) => p.id === workItem.assignedTo)?.name || "",
            }
          : null,
      statusId: workItem?.statusId || null,
      accessLevelId: workItem?.accessLevelId || privateAccessLevelId,
      orgId: orgId,
      parentId: initialParentOption,
    },
  });

  // Fetch authoritative work item by ID when available
  const { data: fetchedWork } = useQuery<WorkModel | null>({
    queryKey: workItem?.id ? queryKeys.getWorkItemById(workItem.id) : ["noop"],
    queryFn: async () => (workItem?.id ? await getWork(workItem.id) : null),
    enabled: !!workItem?.id,
    staleTime: 0,
  });

  // Helper to avoid unnecessary resets that can cause re-render cascades
  const isSameValue = (a: unknown, b: unknown) => {
    if (a instanceof Date && b instanceof Date)
      return a.getTime() === b.getTime();
    return a === b;
  };

  const hasValueProp = (v: unknown): v is { value: unknown } =>
    typeof v === "object" && v !== null && "value" in v;
  const shouldReset = (next: Partial<WorkFormValues>) => {
    const curr = form.getValues();
    // Compare a stable subset of fields
    const keys: (keyof WorkFormValues)[] = [
      "name",
      "workTypeId",
      "tentativeStartDate",
      "tentativeEndDate",
      "actualStartDate",
      "actualEndDate",
      "tentativeBudget",
      "approvedBudget",
      "description",
      "documentation",
      "statusId",
      "accessLevelId",
      "orgId",
      "parentId",
      "assignedTo",
    ];
    for (const k of keys) {
      const n = next[k as keyof WorkFormValues] as unknown;
      const c = curr[k as keyof WorkFormValues] as unknown;
      // Normalize option objects for parentId/assignedTo by their value
      const norm = (v: unknown) => (hasValueProp(v) ? v.value : v);
      if (k === "parentId" || k === "assignedTo") {
        if (!isSameValue(norm(n), norm(c))) return true;
      } else if (!isSameValue(n, c)) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    // When the selected work item (id) changes
    const workItemIdChanged = workItem?.id !== workItemId;
    const src = workItem ?? null;
    const parentFromSrc =
      parentOptions.find(
        (item) => item.value === (src?.parentId as string | undefined),
      ) ?? null;
    // If it's a project (no parent) and no accessLevelId, default to Private
    const isProject = !src?.parentId;
    const defaultAccessLevelId =
      src?.accessLevelId || (isProject ? privateAccessLevelId : null);
    const nextDefaults: Partial<WorkFormValues> = {
      name: src?.name || "",
      workTypeId: src?.workTypeId || "",
      tentativeStartDate: toDate(src?.tentativeStartDate),
      tentativeEndDate: toDate(src?.tentativeEndDate),
      actualStartDate: toDate(src?.actualStartDate),
      actualEndDate: toDate(src?.actualEndDate),
      tentativeBudget: src?.tentativeBudget || null,
      approvedBudget: src?.approvedBudget || null,
      description: src?.description || "",
      documentation: src?.documentation ?? undefined,
      assignedTo: src?.assignedToAgent
        ? {
            value: `agent:${src.assignedToAgent}`,
            label:
              agents.find((a) => a.id === src.assignedToAgent)?.name || "Agent",
          }
        : src?.assignedTo
          ? {
              value: `person:${src.assignedTo}`,
              label: people.find((p) => p.id === src.assignedTo)?.name || "",
            }
          : null,
      statusId: src?.statusId || null,
      accessLevelId: defaultAccessLevelId,
      orgId: orgId,
      parentId: parentFromSrc,
    };
    // Always reset if workItemId changed, or if it's a new project and privateAccessLevelId just became available
    const isNewProject = !workItem?.id && workItemId === undefined;
    const currentAccessLevelId = form.getValues("accessLevelId");
    const shouldUpdateForNewProject =
      isNewProject &&
      privateAccessLevelId &&
      !currentAccessLevelId &&
      defaultAccessLevelId;
    if (workItemIdChanged || shouldUpdateForNewProject) {
      if (workItemIdChanged) {
        setWorkItemId(workItem?.id);
        setFunctionFacetId(src?.productId ?? null);
      }
      if (shouldReset(nextDefaults)) {
        form.reset(nextDefaults);
      }
    }
  }, [workItem?.id, agents.length, people.length, privateAccessLevelId]);

  useEffect(() => {
    // When authoritative data arrives, reset the form with it
    if (fetchedWork && fetchedWork.id === workItemId) {
      const src = fetchedWork;
      const parentFromSrc =
        parentOptions.find(
          (item) => item.value === (src?.parentId as string | undefined),
        ) ?? null;
      // If it's a project (no parent) and no accessLevelId, default to Private
      const isProject = !src?.parentId;
      const defaultAccessLevelId =
        src?.accessLevelId || (isProject ? privateAccessLevelId : null);
      const nextDefaults: Partial<WorkFormValues> = {
        name: src?.name || "",
        workTypeId: src?.workTypeId || "",
        tentativeStartDate: toDate(src?.tentativeStartDate),
        tentativeEndDate: toDate(src?.tentativeEndDate),
        actualStartDate: toDate(src?.actualStartDate),
        actualEndDate: toDate(src?.actualEndDate),
        tentativeBudget: src?.tentativeBudget || null,
        approvedBudget: src?.approvedBudget || null,
        description: src?.description || "",
        documentation: src?.documentation ?? "",
        assignedTo: src?.assignedToAgent
          ? {
              value: `agent:${src.assignedToAgent}`,
              label:
                agents.find((a) => a.id === src.assignedToAgent)?.name ||
                "Agent",
            }
          : src?.assignedTo
            ? {
                value: `person:${src.assignedTo}`,
                label: people.find((p) => p.id === src.assignedTo)?.name || "",
              }
            : null,
        statusId: src?.statusId || null,
        accessLevelId: defaultAccessLevelId,
        orgId: orgId,
        parentId: parentFromSrc,
      };
      if (shouldReset(nextDefaults)) {
        form.reset(nextDefaults);
      }
      setFunctionFacetId(src?.productId ?? null);
    }
  }, [
    fetchedWork,
    workItemId,
    agents.length,
    people.length,
    privateAccessLevelId,
    parentOptions.length,
    orgId,
  ]);

  // Handle dialog open/close
  const handleOpenChange = (newOpenState: boolean) => {
    // When opening the dialog, just update the state
    if (newOpenState) {
      onOpenChange(newOpenState);
      return;
    }

    // When closing, check if there are unsaved changes
    if (form.formState.isDirty) {
      // Show confirmation dialog if form is dirty
      setShowUnsavedChangesDialog(true);
      // Don't close the dialog yet
      return;
    }

    // Close directly if form is not dirty
    onOpenChange(false);
  };

  // Handle confirmation dialog cancel - keep editing
  const handleCancelClose = () => {
    setShowUnsavedChangesDialog(false);
  };

  // // Handle confirmation dialog confirm - discard changes and close
  const handleConfirmClose = () => {
    // First close the confirmation dialog
    setShowUnsavedChangesDialog(false);

    // Then close the main dialog
    onOpenChange(false);

    // Finally reset the form (after dialog is closed)
    form.reset();
  };

  // Handle form submission
  const onSubmit = async (values: WorkFormValues) => {
    if (!workItem) return;

    try {
      // Use fetchedWork if available (has full data), otherwise fall back to workItem prop
      const effectiveWorkItem = fetchedWork || workItem;

      // Extract and map assignee to person or agent
      let selectedAssignee: string | null = null;
      if (values.assignedTo) {
        const val =
          typeof values.assignedTo === "object" && "value" in values.assignedTo
            ? String(values.assignedTo.value)
            : String(values.assignedTo);
        selectedAssignee = val || null;
      }

      // Handle the parentId which can be an object or string
      let parentId = null;

      if (values.parentId) {
        // Check if it's an object with value property (from AsyncComboboxFormField)
        if (typeof values.parentId === "object" && "value" in values.parentId) {
          parentId =
            values.parentId.value === "no-parent"
              ? null
              : values.parentId.value;
        } else {
          // It's a string
          parentId = values.parentId === "no-parent" ? null : values.parentId;
        }
      }

      const workModel = await createWorkModelFromFormValues(
        {
          ...values,
          // assignedTo handled below to support agents
        },
        orgId,
        parentId, // Use the processed parentId value
        projectId,
        effectiveWorkItem,
      );

      // Normalize to assignedTo or assignedToAgent
      let personId: string | null = null;
      let agentId: string | null = null;
      if (selectedAssignee && selectedAssignee.includes(":")) {
        const [kind, id] = selectedAssignee.split(":");
        if (kind === "agent") {
          agentId = id;
        } else if (kind === "person") {
          personId = id;
        }
      }

      workModel.assignedTo = personId;
      workModel.assignedToAgent = agentId;
      workModel.isAgentWork = !!agentId;

      const isProjectRoot = !effectiveWorkItem?.parentId && !parentId;
      if (isProjectRoot) {
        workModel.productId = functionFacetId;
      }

      await updateWork(workModel);

      // Show success toast
      toast.success("Work item updated successfully", {
        duration: 3000,
      });

      // Close the dialog
      // onOpenChange(false);

      // Notify parent
      //onWorkUpdated();

      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkTreeById(projectId as string),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItem(projectId as string),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkItemById(effectiveWorkItem?.id || ""),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItems(orgId),
      });
      if (isProjectRoot) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectProductContext(orgId, projectId as string),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.assignableFunctionFacets(orgId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.getProjectNavItems(orgId),
        });
        queryClient.invalidateQueries({
          queryKey: ["masterWorkItems", orgId],
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.productFacetWorksByOrgId(orgId),
        });
        queryClient.invalidateQueries({
          queryKey: ["productIntelligence", orgId],
        });
        queryClient.invalidateQueries({
          queryKey: ["productMaturitySummary", orgId],
        });
        queryClient.invalidateQueries({
          queryKey: ["productProjectsExecution", orgId],
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.productsHubSummaries(orgId),
        });
        queryClient.invalidateQueries({
          queryKey: ["linkedFacetIdsForProduct", orgId],
        });
        queryClient.invalidateQueries({
          queryKey: ["workProductFacetContext", orgId],
        });
      }

      onSuccess?.();
    } catch (error) {
      console.error("Error updating work:", error);

      // Show error toast
      toast.error("Failed to update work item", {
        duration: 3000,
      });
    }
  };

  const handleDelete = async () => {
    try {
      if (workItem?.id) {
        await deleteWork(workItem?.id);
      }
      toast.success("Work item deleted successfully", {
        duration: 3000,
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkTreeById(projectId as string),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItem(projectId as string),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkItemById(workItem?.id || ""),
      });
      // Invalidate masterWorkItems query used by sidebar for filtering (only for projects)
      if (!workItem?.parentId) {
        queryClient.invalidateQueries({
          queryKey: ["masterWorkItems", orgId],
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.getProjectNavItems(orgId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItems(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectNavItems(orgId),
      });

      // Close the dialog and notify parent component
      setDeleteModalOpen(false);

      // Always close the form first
      onOpenChange(false);

      // Then call onDelete callback to refresh table-tree data
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error("Failed to delete work item:", error);
      toast.error("Failed to delete work item", {
        duration: 3000,
      });
      setDeleteModalOpen(false);
    }
  };

  // Determine if this is a project (no parent) - compute before early return
  const parentField = form.watch("parentId");
  const parentFromForm = hasValueProp(parentField)
    ? parentField.value
    : parentField;
  const hasParent = Boolean(
    (fetchedWork && fetchedWork.parentId) ??
      workItem?.parentId ??
      parentFromForm,
  );
  const isProject = !hasParent;

  const persistDocumentation = useCallback(
    async (doc: string): Promise<void> => {
      const base = fetchedWork || workItem;
      if (!base?.id) return;
      form.setValue("documentation", doc);
      await updateWork({ ...base, documentation: doc });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkItemById(base.id),
      });
    },
    [fetchedWork, workItem, form, queryClient],
  );

  const isDetailsDocsSplit = layoutMode === "details-docs-split";
  const documentationExternallyRendered = Boolean(
    documentationHeaderHostRef || isDetailsDocsSplit,
  );

  const documentationField = (
    <DocumentationField
      control={form.control}
      workId={fetchedWork?.id ?? workItem?.id}
      workTypes={workTypes}
      isProjectRoot={isProject}
      persistDocumentation={persistDocumentation}
      active={open}
      compact={documentationExternallyRendered}
      variant={isDetailsDocsSplit ? "inline" : documentationVariant}
    />
  );

  // Portal docs CTA into a stable header host (ref object — no parent setState).
  const [docsPortalHost, setDocsPortalHost] = useState<HTMLElement | null>(
    null,
  );
  useLayoutEffect(() => {
    if (!documentationHeaderHostRef || !open) {
      setDocsPortalHost(null);
      return;
    }
    const attach = (): void => {
      const el = documentationHeaderHostRef.current;
      setDocsPortalHost((prev) => (prev === el ? prev : el));
    };
    attach();
    // Sheet header may commit one frame after the form mounts.
    const frameId = requestAnimationFrame(attach);
    return () => cancelAnimationFrame(frameId);
  }, [documentationHeaderHostRef, open, workItem?.id, fetchedWork?.id]);

  // Use external workAccessLevels if provided, otherwise use internal
  const effectiveWorkAccessLevels =
    externalWorkAccessLevels || workAccessLevels || [];
  const effectiveIsWorkAccessLevelsLoading =
    externalIsWorkAccessLevelsLoading ?? isWorkAccessLevelsLoading;

  const accessLevelId = form.watch("accessLevelId");
  const currentAccessLevel = effectiveWorkAccessLevels.find(
    (level) => level.id === accessLevelId,
  );
  const currentAccessLevelName = currentAccessLevel?.name || "None";

  const handleWorkAccessLevelChange = useCallback(
    (value: string | null) => {
      // If the value is different from current, show confirmation dialog
      if (value !== accessLevelId) {
        setPendingAccessLevelId(value);
        setShowSecurityChangeDialog(true);
      }
    },
    [accessLevelId],
  );

  const handleConfirmSecurityChange = async () => {
    if (!workItem || pendingAccessLevelId === null) return;

    try {
      const effectiveWorkItem = fetchedWork || workItem;
      const newAccessLevel = effectiveWorkAccessLevels.find(
        (level) => level.id === pendingAccessLevelId,
      );
      const newAccessLevelName = newAccessLevel?.name || "Unknown";

      // Create a minimal work model with just the access level change
      const workModel = await createWorkModelFromFormValues(
        {
          ...form.getValues(),
          accessLevelId: pendingAccessLevelId,
        },
        orgId,
        effectiveWorkItem.parentId,
        projectId,
        effectiveWorkItem,
      );

      workModel.assignedTo = effectiveWorkItem.assignedTo;
      workModel.assignedToAgent = effectiveWorkItem.assignedToAgent;
      workModel.isAgentWork = effectiveWorkItem.isAgentWork || false;
      await updateWork(workModel);

      // Update the form value
      form.setValue("accessLevelId", pendingAccessLevelId, {
        shouldDirty: false,
      });

      // Show success toast
      toast.success(
        `Security level changed to ${newAccessLevelName} successfully`,
        {
          duration: 3000,
        },
      );

      // Close dialog and reset pending value
      setShowSecurityChangeDialog(false);
      setPendingAccessLevelId(null);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkTreeById(projectId as string),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItem(projectId as string),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkItemById(effectiveWorkItem?.id || ""),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItems(orgId),
      });

      onSuccess?.();
    } catch (error) {
      console.error("Error updating security level:", error);
      toast.error("Failed to update security level", {
        duration: 3000,
      });
      setShowSecurityChangeDialog(false);
      setPendingAccessLevelId(null);
    }
  };

  const handleCancelSecurityChange = () => {
    setShowSecurityChangeDialog(false);
    setPendingAccessLevelId(null);
  };

  // Track previous values to prevent unnecessary updates
  const prevTitleActionsRef = useRef<{
    isProject: boolean;
    accessLevelId: string | null | undefined;
    optionsLength: number;
  } | null>(null);

  // Update title actions when form state changes
  useEffect(() => {
    if (!onTitleActionsChange) return;

    const currentState = {
      isProject,
      accessLevelId,
      optionsLength: effectiveWorkAccessLevels.length,
    };

    // Only update if something actually changed
    const prevState = prevTitleActionsRef.current;
    if (
      prevState &&
      prevState.isProject === currentState.isProject &&
      prevState.accessLevelId === currentState.accessLevelId &&
      prevState.optionsLength === currentState.optionsLength
    ) {
      return;
    }

    prevTitleActionsRef.current = currentState;

    if (isProject) {
      onTitleActionsChange(
        <WorkAccessLevelBadgeDropdown
          value={accessLevelId}
          options={effectiveWorkAccessLevels}
          onValueChange={handleWorkAccessLevelChange}
          disabled={effectiveIsWorkAccessLevelsLoading}
        />,
      );
    } else {
      onTitleActionsChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isProject,
    accessLevelId,
    effectiveWorkAccessLevels.length,
    effectiveIsWorkAccessLevelsLoading,
    handleWorkAccessLevelChange,
  ]);

  return (
    <>
      {docsPortalHost ? createPortal(documentationField, docsPortalHost) : null}
      <DeleteConfirmation
        onConfirm={handleDelete}
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={isDetailsDocsSplit ? undefined : "space-y-6"}
        >
          <div
            className={
              isDetailsDocsSplit
                ? "grid items-stretch gap-8 lg:grid-cols-2"
                : undefined
            }
          >
            <div
              className={
                isDetailsDocsSplit
                  ? "flex min-w-0 flex-col gap-8"
                  : undefined
              }
            >
            <div
              className={
                isDetailsDocsSplit
                  ? "min-w-0 space-y-6 rounded-lg border bg-background p-4"
                  : "space-y-6"
              }
            >
              {/* Basic Information */}
              <div className="grid gap-4 sm:grid-cols-2">
                {(() => {
                  // Determine if this is a project (no parent) or a child task/phase (has parent)
                  const parentField = form.watch("parentId");
                  const parentFromForm = hasValueProp(parentField)
                    ? parentField.value
                    : parentField;
                  const hasParent = Boolean(
                    (fetchedWork && fetchedWork.parentId) ??
                      workItem?.parentId ??
                      parentFromForm,
                  );
                  return hasParent;
                })() && (
                  <>
                    <div className="sm:col-span-1">
                      <ComboboxFormField<WorkFormValues>
                        name="parentId"
                        label="Parent"
                        placeholder="Search for parent"
                        items={parentOptions}
                        showAll
                      />
                    </div>

                    <SelectField
                      control={form.control}
                      name="workTypeId"
                      label="Work Type"
                      placeholder="Select work type"
                      options={workTypes || []}
                      disabled={isWorkTypesLoading}
                    />
                  </>
                )}
                {!documentationExternallyRendered ? (
                  <div className="sm:col-span-2">{documentationField}</div>
                ) : null}
                <div className="sm:col-span-2">
                  <TextInputField
                    control={form.control}
                    name="name"
                    label="Name"
                    placeholder="Enter work item name"
                  />
                </div>

                {isProject ? (
                  <div className="sm:col-span-2">
                    <ProjectFunctionAssignmentField
                      value={functionFacetId}
                      onChange={setFunctionFacetId}
                    />
                  </div>
                ) : null}

                {(() => {
                  const unassigned = [{ label: "Unassigned", value: "" }];
                  const peopleOptions = (people || []).map((p) => ({
                    value: `person:${p.id}`,
                    label: `${p.firstName || ""} ${p.lastName || ""}`.trim(),
                  }));
                  const agentOptions = (agents || []).map((a) => ({
                    value: `agent:${a.id}`,
                    label: a.name || "Agent",
                  }));
                  const assigneeOptions = [
                    ...unassigned,
                    ...peopleOptions,
                    ...agentOptions,
                  ];
                  return (
                    <ComboboxFormField<WorkFormValues>
                      name="assignedTo"
                      label="Assigned To"
                      placeholder="Search for a person or agent"
                      items={assigneeOptions}
                      showAll
                    />
                  );
                })()}
                <SelectField
                  control={form.control}
                  name="statusId"
                  label="Status"
                  placeholder="Select status"
                  options={workStatuses || []}
                  disabled={isWorkStatusesLoading}
                />

                <DatePickerField
                  control={form.control}
                  name="tentativeStartDate"
                  label="Planned Start Date"
                />

                <DatePickerField
                  control={form.control}
                  name="tentativeEndDate"
                  label="Planned End Date"
                />

                <DatePickerField
                  control={form.control}
                  name="actualStartDate"
                  label="Actual Start Date"
                />

                <DatePickerField
                  control={form.control}
                  name="actualEndDate"
                  label="Actual End Date"
                />

                <NumberField
                  control={form.control}
                  name="tentativeBudget"
                  label="Planned Budget"
                  placeholder="Enter planned budget"
                  formatAsCurrency={true}
                  currencyCode={settings?.currencyCode}
                />

                <NumberField
                  control={form.control}
                  name="approvedBudget"
                  label="Approved Budget"
                  placeholder="Enter approved budget"
                  formatAsCurrency={true}
                  currencyCode={settings?.currencyCode}
                />

                <div className="sm:col-span-2">
                  <TextareaField
                    control={form.control}
                    name="description"
                    label="Description"
                    placeholder={
                      workItem?.parentId
                        ? "Enter work item description"
                        : "Enter project description"
                    }
                  />
                </div>
              </div>
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteModalOpen(true)}
                  className="h-6 gap-1 px-2 text-[11px] [&_svg]:size-3"
                >
                  <Trash2 />
                  Delete
                </Button>
                <div className="flex gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    disabled={form.formState.isSubmitting}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
            {isDetailsDocsSplit && leftColumnFooter ? leftColumnFooter : null}
            </div>

            {isDetailsDocsSplit ? (
              <div className="flex min-h-0 min-w-0 flex-col">
                <div className="flex h-full flex-col rounded-lg border bg-background p-4">
                  <div className="flex min-h-0 flex-1 flex-col">
                    {documentationField}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </form>
      </Form>

      {/* Unsaved Changes Confirmation Dialog */}
      <AlertDialog
        open={showUnsavedChangesDialog}
        onOpenChange={setShowUnsavedChangesDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close this
              dialog? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Keep Editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Security Level Change Confirmation Dialog */}
      <AlertDialog
        open={showSecurityChangeDialog}
        onOpenChange={setShowSecurityChangeDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Security Level</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>You are about to change the security level of this project.</p>
              <div className="flex items-center gap-2 rounded-md border p-3">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Current Level</p>
                  <p className="font-medium">{currentAccessLevelName}</p>
                </div>
                <div className="text-muted-foreground">→</div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">New Level</p>
                  <p className="font-medium">
                    {effectiveWorkAccessLevels.find(
                      (level) => level.id === pendingAccessLevelId,
                    )?.name || "Unknown"}
                  </p>
                </div>
              </div>
              <p className="text-sm">
                This change will be saved immediately. Are you sure you want to
                proceed?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelSecurityChange}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSecurityChange}>
              Change Security Level
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
